import React, { useEffect, useState } from 'react';
import { CoveoContext } from './context';
import { clearAllFacetsContext } from '../../../helpers/coveoFacet';

export const BreadcrumbManager = props => {
  const { controller, filtersPerFacetValue, headlessEngine, defaultTab } =
    props;
  const { labels } = props;
  const { valueNameOverride } = props;
  const [state, setState] = useState(controller.state);

  useEffect(() => controller.subscribe(() => setState(controller.state)), []);

  const noFiltersAppliedBox = () => {
    return (
      <div className="facet-breadcrumbs facet-breadcrumbs--no-results">
        <div className="search-filter-row__applied-header">
          <h3 className="facet-breadcrumbs__heading search-filter-row__applied-heading">
            {labels.narrowResults}
          </h3>
          <button
            className="facet-breadcrumbs__clear-all link-siplast link search-filter-row__clear-btn facet-breadcrumbs__clear-all--disabled"
            onClick={() => {
              clearAllFacetsContext(
                filtersPerFacetValue,
                headlessEngine,
                defaultTab
              );
              controller.deselectAll();
            }}
            disabled={true}
          >
            {labels.clearAll}
          </button>
        </div>
        <div className="search-filter-row__applied-filters">
          <div className="search-filter-row__applied-filters-empty">
            {labels.noFiltersApplied}
          </div>
        </div>
      </div>
    );
  };

  if (!state?.hasBreadcrumbs) {
    return noFiltersAppliedBox();
  }

  const getNameOverride = name => {
    const override = valueNameOverride.filter(obj => {
      return obj.value === name;
    });
    return override.length ? override[0].label : name;
  };

  const handleFacetClick = breadcrumb => {
    if (!filtersPerFacetValue?.length) {
      breadcrumb.deselect();
      return;
    }

    // Check if the value directly matches a context facet
    const directContextMatch = filtersPerFacetValue.find(
      facet =>
        facet.facetValue?.toLowerCase() ===
        breadcrumb?.value?.value?.toLowerCase()
    );

    if (directContextMatch) {
      // Remove context if it exists
      const { context, tab } = directContextMatch;
      if (context) {
        const coveoContext = CoveoContext(headlessEngine);
        Object.keys(context).forEach(key => coveoContext?.remove(key));
      }

      if (tab) {
        defaultTab?.select();
      }
    } else {
      // Find the facet type that contains this value
      const matchingFacetType = state.facetBreadcrumbs.find(facet =>
        facet.values?.some(facetValue => {
          const _facetValue = facetValue?.value?.value;
          const _breadcrumbValue = breadcrumb?.value?.value;

          return _facetValue?.toLowerCase() === _breadcrumbValue?.toLowerCase();
        })
      );

      if (!matchingFacetType) {
        breadcrumb.deselect();
        return;
      }

      // Find if any of the facet values match a context facet
      const matchingContextFacet = filtersPerFacetValue.find(facet =>
        matchingFacetType.values.some(facetValue => {
          const _facetValue = facetValue?.value?.value;
          const _facetValue2 = facet.facetValue;
          return _facetValue?.toLowerCase() === _facetValue2?.toLowerCase();
        })
      );

      // If we have a matching facet type with exactly 2 values and a matching context facet
      if (matchingContextFacet && matchingFacetType.values.length === 2) {
        // Get the full context object for the matching facet
        const contextObject = filtersPerFacetValue.find(
          facet =>
            facet.facetValue?.toLowerCase() ===
            matchingContextFacet.facetValue?.toLowerCase()
        );

        // Add the context if it exists
        if (contextObject?.context) {
          const coveoContext = CoveoContext(headlessEngine);
          Object.entries(contextObject.context).forEach(([key, val]) =>
            coveoContext?.add(key, val)
          );
        }
      }
    }

    breadcrumb.deselect();
  };

  return (
    <div className="facet-breadcrumbs">
      <div className="search-filter-row__applied-header">
        <h3 className="facet-breadcrumbs__heading search-filter-row__applied-heading">
          {labels.narrowResults}
        </h3>
        <button
          className="facet-breadcrumbs__clear-all link-siplast link search-filter-row__clear-btn"
          onClick={() => {
            clearAllFacetsContext(
              filtersPerFacetValue,
              headlessEngine,
              defaultTab
            );
            controller.deselectAll();
          }}
        >
          {labels.clearAll}
        </button>
      </div>

      <div className="facet-breadcrumbs__breadcrumbs search-filter-row__applied-filters">
        {state.categoryFacetBreadcrumbs.length > 0 &&
          state.categoryFacetBreadcrumbs.map(facet =>
            facet.path.map(breadcrumb => (
              <button
                className="facet-breadcrumbs__breadcrumb search-filter-row__applied-filter-btn"
                key={breadcrumb.value}
                onClick={() =>
                  breadcrumb.path.length > 1
                    ? window.dispatchEvent(
                      new CustomEvent('deselectFacetTree', {
                        detail: {
                          value: facet.path[breadcrumb.path.length - 2]
                        }
                      })
                    )
                    : facet.deselect()
                }
              >
                <svg
                  viewBox="0 0 120 120"
                  className="search-filter-row__applied-filter-icon"
                  width="24"
                  height="24"
                  role="presentation"
                >
                  <use xlinkHref="#close-regular"></use>
                </svg>
                {breadcrumb.value}
              </button>
            ))
          )}
        {state.facetBreadcrumbs.length > 0 &&
          state.facetBreadcrumbs.map(facet =>
            facet.values.map(breadcrumb => (
              <button
                className="facet-breadcrumbs__breadcrumb search-filter-row__applied-filter-btn"
                key={breadcrumb.value.value}
                onClick={() => handleFacetClick(breadcrumb)}
              >
                <svg
                  viewBox="0 0 120 120"
                  className="search-filter-row__applied-filter-icon"
                  width="24"
                  height="24"
                  role="presentation"
                >
                  <use xlinkHref="#close-regular"></use>
                </svg>
                {valueNameOverride?.length > 0
                  ? getNameOverride(breadcrumb.value.value)
                  : breadcrumb.value.value}
              </button>
            ))
          )}
      </div>
    </div>
  );
};

// usage

/**
 * ```tsx
 * const controller = buildBreadcrumbManager(engine);
 *
 * <BreadcrumbManager controller={controller} />;
 * ```
 */
